/**
 * Resolution Module — shared output-resolution selector for video tools.
 *
 * The video tools used to silently downscale to hardcoded values (1080p
 * watermark export, 1280p bg-remover canvas, no 1440p in compressor).
 * Users uploading 2K/4K source got 1080p back without any UI hint.
 *
 * This module gives every video tool the same source-aware selector:
 *   · Original (no scale, true to source)
 *   · 4K   — shorter side ≤ 2160  (3840×2160 landscape, 2160×3840 portrait)
 *   · 2K   — shorter side ≤ 1440
 *   · 1080p — shorter side ≤ 1080
 *   · 720p  — shorter side ≤ 720
 *   · 480p  — shorter side ≤ 480
 *   · 360p  — shorter side ≤ 360
 *
 * Industry convention: "Np" = the shorter dimension after scaling. This
 * makes portrait/landscape/square symmetric — 1080p portrait = 1080×1920
 * (TikTok/Reels), 1080p landscape = 1920×1080 (YouTube). One label, both
 * orientations. Aspect ratio is always preserved; we never stretch.
 *
 * Two output paths a tool can use:
 *   · FFmpeg pipeline → ResolutionModule.getScaleFilter() returns a filter
 *     string (or null when no downscale needed). Uses `-2` so FFmpeg picks
 *     the complementary dimension AND rounds to even (x264/x265/VP9 hard
 *     requirement).
 *   · Canvas / WebCodecs → ResolutionModule.getOutputDimensions() returns
 *     {width, height, didDownscale}. Width/height are always even.
 *
 * Source detection:
 *   · Caller-driven (preferred): tool already has probe.width/probe.height
 *     from FFmpegProcessor.probeMedia or <video>.videoWidth/Height.
 *     Pass via setDetectedSource(w, h) — sync, instant.
 *   · Async fallback: detectSource(file) creates a hidden <video>, reads
 *     loadedmetadata, then revokes the object URL. Used when the tool has
 *     no probe of its own yet.
 *
 * Upscale guard: options whose target shorter-side > source shorter-side
 * are disabled in the dropdown (and silently capped if the user somehow
 * selects them programmatically). No upscale ever — upscaling video is a
 * quality regression by definition.
 *
 * Usage in Blade:
 *   @include('components.resolution-select', ['tool' => 'video-compressor'])
 *
 * Usage in JS:
 *   ResolutionModule.init({ tool: 'video-compressor' });
 *   ResolutionModule.setDetectedSource(probe.width, probe.height);
 *   var args = ['-i', input, '-vf', ResolutionModule.getScaleFilter() || 'null'];
 *   //  or for canvas:  var dim = ResolutionModule.getOutputDimensions();
 */
(function(){
'use strict';

// Every locale from config('app.supported_locales') — 9 since fr landed. The
// old "8 locales" note here is exactly how the fr gap shipped: the list is now
// guarded by tests/Unit/InlineLocaleLabelCompletenessTest.
var LABELS = {
    resolution: { en: 'Resolution',     ru: 'Разрешение',    ko: '해상도',          ar: 'الدقة',                pt: 'Resolução',       id: 'Resolusi',       de: 'Auflösung',     es: 'Resolución',   fr: 'Résolution', uk: 'Роздільність', tr: 'Çözünürlük' },
    original:   { en: 'Original',       ru: 'Исходное',       ko: '원본',            ar: 'الأصلي',                pt: 'Original',         id: 'Asli',            de: 'Original',       es: 'Original',     fr: 'Original', uk: 'Оригінал', tr: 'Orijinal' },
    source:     { en: 'Source',         ru: 'Источник',       ko: '소스',            ar: 'المصدر',                pt: 'Origem',           id: 'Sumber',          de: 'Quelle',         es: 'Origen',       fr: 'Source', uk: 'Джерело', tr: 'Kaynak' },
    output:     { en: 'Output',         ru: 'Результат',      ko: '출력',            ar: 'الإخراج',               pt: 'Saída',            id: 'Keluaran',        de: 'Ausgabe',        es: 'Salida',       fr: 'Sortie', uk: 'Результат', tr: 'Çıktı' },
    // Names the source instead of comparing to it — "source: 720p" cannot be
    // misread the way the channel twin's "more than source" was (a user took
    // that one for file size, 2026-07-29). {n} is the source's shorter side.
    upscale_hint:{en: 'source: :np', ru: 'исходник: :np', ko: '원본: :np', ar: 'المصدر: :np',     pt: 'origem: :np', id: 'sumber: :np', de: 'Quelle: :np', es: 'origen: :np', fr: 'source : :np', uk: 'джерело: :np', tr: 'kaynak: :np' },
    upscale_arrow:{en: 'upscale',           ru: 'апскейл',         ko: '업스케일',          ar: 'تحسين',             pt: 'upscale',          id: 'upscale',         de: 'Upscaling',      es: 'upscale',      fr: 'upscale', uk: 'апскейл', tr: 'büyütme' },
    cls_4k:     { en: '4K UHD',         ru: '4K UHD',         ko: '4K UHD',         ar: '4K UHD',                pt: '4K UHD',           id: '4K UHD',          de: '4K UHD',         es: '4K UHD',       fr: '4K UHD', uk: '4K UHD', tr: '4K UHD' },
    cls_2k:     { en: '2K QHD',         ru: '2K QHD',         ko: '2K QHD',         ar: '2K QHD',                pt: '2K QHD',           id: '2K QHD',          de: '2K QHD',         es: '2K QHD',       fr: '2K QHD', uk: '2K QHD', tr: '2K QHD' },
    cls_fhd:    { en: 'Full HD',        ru: 'Full HD',        ko: 'Full HD',        ar: 'Full HD',               pt: 'Full HD',          id: 'Full HD',         de: 'Full HD',        es: 'Full HD',      fr: 'Full HD', uk: 'Full HD', tr: 'Full HD' },
    cls_hd:     { en: 'HD',             ru: 'HD',             ko: 'HD',             ar: 'HD',                    pt: 'HD',               id: 'HD',              de: 'HD',             es: 'HD',           fr: 'HD', uk: 'HD', tr: 'HD' },
    cls_sd:     { en: 'SD',             ru: 'SD',             ko: 'SD',             ar: 'SD',                    pt: 'SD',               id: 'SD',              de: 'SD',             es: 'SD',           fr: 'SD', uk: 'SD', tr: 'SD' },
    cls_low:    { en: 'Low',            ru: 'Низкое',         ko: '낮음',            ar: 'منخفض',                 pt: 'Baixa',            id: 'Rendah',          de: 'Niedrig',        es: 'Baja',         fr: 'Basse', uk: 'Низька', tr: 'Düşük' },
    // NB: unlike the pre-existing labels above, these two carry `fr` — the rest
    // of this LABELS object is still 8-locale (pre-existing gap: French falls
    // back to English for every other key here). Ours must not, so fr is set.
    premium:    { en: 'Premium',        ru: 'Премиум',        ko: '프리미엄',        ar: 'بريميوم',               pt: 'Premium',          id: 'Premium',         de: 'Premium',        es: 'Premium',        fr: 'Premium', uk: 'Преміум', tr: 'Premium' },
    // "· up to 720p" style suffix on the source-match option so a free user
    // SEES that "Original" is capped (Farid 2026-07-19: cap source too) — the
    // cap must never be silent (feedback_no_silent_quality_downgrade).
    upto:       { en: 'up to :np',      ru: 'до :np',         ko: '최대 :np',        ar: 'حتى :np',               pt: 'até :np',          id: 'hingga :np',      de: 'bis :np',        es: 'hasta :np',      fr: 'jusqu’à :np', uk: 'до :np', tr: 'en fazla :np' },
    // Tool resize ceiling (maxUpscaleDim): «plain resize stops at :np — above
    // that only AI upscale adds pixels». Not a plan lock, not a source lock.
    upscale_cap:{ en: 'resize up to :np only', ru: 'ресайз только до :np', ko: '리사이즈는 :np까지만', ar: 'تغيير الحجم حتى :np فقط', pt: 'redimensionar só até :np', id: 'ubah ukuran hanya sampai :np', de: 'Resize nur bis :np', es: 'redimensionar solo hasta :np', fr: 'redimensionner jusqu’à :np seulement', uk: 'ресайз лише до :np', tr: 'yalnızca :np’ye kadar' }
};

// Default presets. Each entry: { value: 'original'|<int>, key: shorter-side }.
// Order = display order in the dropdown.
var DEFAULT_PRESETS = [
    { value: 'original' },
    { value: 2160 },
    { value: 1440 },
    { value: 1080 },
    { value: 720 },
    { value: 480 },
    { value: 360 }
];

var _locale = (function(){
    var loc = (window.__LOCALE || document.documentElement.lang || 'en').toString();
    return loc.substring(0, 2).toLowerCase();
})();

function tr(key, repl) {
    var entry = LABELS[key];
    if (!entry) return key;
    var s = entry[_locale] || entry.en || key;
    if (repl) { Object.keys(repl).forEach(function(k){ s = s.replace(':' + k, repl[k]); }); }
    return s;
}

// Plan export-height ceiling (window.__qualityTier.videoMaxHeight()). Infinity
// for Pro OR when the primitive/tier isn't ready — i.e. exactly the cases where
// nothing should be capped (fail-open). For a free user it is video_max_height
// (720). Read LIVE, never cached: the tier snapshot lands async and Premium can
// resolve late, so a cached value would strand a paying user at the guest cap.
// NB: Pro must be Infinity, NOT the raw 2160 — else a Pro user's 8K "Original"
// source would be silently downcapped to 4K, a regression for the tier that pays.
function planCeiling() {
    if (!window.__qualityTier || window.__qualityTier.isPro()) return Infinity;
    var h = window.__qualityTier.videoMaxHeight();
    return (typeof h === 'number' && h > 0) ? h : Infinity;
}

// ── Pure helpers (no module state) ────────────────────────────────────

function classify(width, height) {
    var shorter = Math.min(width || 0, height || 0);
    if (shorter >= 2160) return tr('cls_4k');
    if (shorter >= 1440) return tr('cls_2k');
    if (shorter >= 1080) return tr('cls_fhd');
    if (shorter >= 720)  return tr('cls_hd');
    if (shorter >= 480)  return tr('cls_sd');
    return tr('cls_low');
}

function formatDimensions(w, h) {
    // A WxH string is LTR data, but it lands inside RTL (Arabic) UI — embedded in
    // the source badge text AND in <option> labels, where CSS (direction/
    // unicode-bidi) can't reach into the native dropdown. Without isolation the
    // bidi algorithm reorders the two number runs and "640×360" renders reversed
    // as "360×640". Wrap in Unicode bidi isolates (U+2066 LRI … U+2069 PDI) so the
    // dimension is a self-contained LTR run in any context. Display-only — real
    // dims come from getOutputDimensions(), so the control chars are inert.
    return '⁦' + Math.round(w) + '×' + Math.round(h) + '⁩';
}

// Compute output dimensions for a target shorter-side. Always preserves
// aspect ratio, always returns even pixels (x264/x265/VP9 hard requirement —
// odd dims fail with "width/height not divisible by 2").
//
// Default behavior: never upscales (capped: true when target > source).
// V2.A allowUpscale: pass {allowUpscale: true} as 4th opts arg to enable
// upscaling — used by tools whose primary purpose IS upscaling (e.g.
// enhance-video with Real-ESRGAN x4). Result includes didUpscale: true so
// the badge / telemetry can distinguish from downscale.
function computeFor(srcW, srcH, value, opts) {
    opts = opts || {};
    srcW = Math.max(0, Math.round(srcW || 0));
    srcH = Math.max(0, Math.round(srcH || 0));
    if (!srcW || !srcH) return { width: 0, height: 0, didDownscale: false, didUpscale: false, capped: false };

    var sourceEven = { width: srcW - (srcW % 2), height: srcH - (srcH % 2) };

    if (value === 'original' || value === '0' || value == null || value === '') {
        return { width: sourceEven.width, height: sourceEven.height, didDownscale: false, didUpscale: false, capped: false };
    }
    var N = parseInt(value, 10);
    if (!N || N <= 0) {
        return { width: sourceEven.width, height: sourceEven.height, didDownscale: false, didUpscale: false, capped: false };
    }

    var shorter = Math.min(srcW, srcH);
    // Target equals source — no scaling needed either direction.
    if (shorter === N) {
        return { width: sourceEven.width, height: sourceEven.height, didDownscale: false, didUpscale: false, capped: false };
    }
    // Target larger than source = potential upscale.
    if (shorter < N) {
        if (!opts.allowUpscale) {
            // Default: silently cap at source. Dropdown also disables these
            // options when the module is wired to a select; this is the
            // belt-and-suspenders fallback for direct API calls.
            return { width: sourceEven.width, height: sourceEven.height, didDownscale: false, didUpscale: false, capped: true };
        }
        // Upscale path — same aspect-preserving math, just no clamp.
        var upScale = N / shorter;
        var uw = Math.round(srcW * upScale);
        var uh = Math.round(srcH * upScale);
        if (uw % 2 !== 0) uw--;
        if (uh % 2 !== 0) uh--;
        if (uw < 2) uw = 2;
        if (uh < 2) uh = 2;
        return { width: uw, height: uh, didDownscale: false, didUpscale: true, capped: false };
    }

    // Target smaller than source — downscale path (always allowed).
    var scale = N / shorter;
    var w = Math.round(srcW * scale);
    var h = Math.round(srcH * scale);
    if (w % 2 !== 0) w--;
    if (h % 2 !== 0) h--;
    if (w < 2) w = 2;
    if (h < 2) h = 2;
    return { width: w, height: h, didDownscale: true, didUpscale: false, capped: false };
}

// Universal aspect-aware FFmpeg filter, even pixels.
//   landscape (iw > ih): scale=-2:N            — fix shorter (height), width auto
//   portrait  (ih ≥ iw): scale=N:-2            — fix shorter (width), height auto
// `-2` tells FFmpeg "compute and round to nearest even number".
//
// Default (no opts.allowUpscale): wraps target in `min(N, iw|ih)` so 4K-target
// on a 1080p source becomes a no-op rather than an upscale.
//
// V2.A allowUpscale: drops the `min()` clamp so target N is honored even when
// source is smaller. Used by upscale tools (enhance-video / Real-ESRGAN x4).
function buildScaleFilter(targetN, opts) {
    opts = opts || {};
    var n = parseInt(targetN, 10);
    if (!n || n <= 0) return null;
    if (opts.allowUpscale) {
        return "scale=w='if(gte(ih\\,iw)\\," + n + "\\,-2)':h='if(gte(ih\\,iw)\\,-2\\," + n + ")':flags=lanczos";
    }
    return "scale=w='if(gte(ih\\,iw)\\,min(" + n + "\\,iw)\\,-2)':h='if(gte(ih\\,iw)\\,-2\\,min(" + n + "\\,ih))':flags=lanczos";
}

// ── Module state ──────────────────────────────────────────────────────

var _selectEl = null;
var _fieldEl = null;
var _labelEl = null;
var _badgeEl = null;
var _toolName = '';
var _presets = DEFAULT_PRESETS.slice();
var _detected = { width: 0, height: 0 };
var _suppressTelemetry = false;
// V2.A: when true, rebuildOptions doesn't disable larger-than-source options
// and getScaleFilter / getOutputDimensions allow upscale. Set per-tool via
// init({ allowUpscale: true }) — used by tools whose primary purpose IS
// upscaling (enhance-video / Real-ESRGAN x4). Default false preserves the
// safe behaviour for every other tool.
var _allowUpscale = false;
// V2.B: when set, rebuildOptions hides preset entries whose target shorter-side
// exceeds N. Used by tools with hard processing-pipeline caps (e.g. AI model
// max input dim). Distinguished from source-driven disable: maxOutputDim is a
// TOOL invariant (model can't go higher), source-disable is a USER condition
// (depends on what they uploaded). Default 0 = no cap.
var _maxOutputDim = 0;
// V2.C: when set with allowUpscale, upscale is permitted but only up to N —
// options that would upscale ABOVE source AND above N are disabled-with-hint
// (not skipped — downscaling to them from a larger source stays allowed).
// enhance-video uses this: a plain resize is fine up to 1080p, but real
// 1440p/4K enlargement is the dedicated AI Upscale feature's job. 0 = no cap.
var _maxUpscaleDim = 0;

function buildOptionLabel(value, srcW, srcH) {
    if (value === 'original') {
        if (srcW && srcH) {
            return tr('original') + ' (' + formatDimensions(srcW, srcH) + ')';
        }
        return tr('original');
    }
    var n = parseInt(value, 10);
    if (n === 2160) return '4K — 2160p';
    if (n === 1440) return '2K — 1440p';
    if (n === 1080) return 'Full HD — 1080p';
    if (n === 720)  return 'HD — 720p';
    if (n === 480)  return 'SD — 480p';
    return n + 'p';
}

function rebuildOptions() {
    if (!_selectEl) return;
    var prev = _selectEl.value;
    var srcShorter = Math.min(_detected.width || 0, _detected.height || 0);
    var ceiling = planCeiling();   // Infinity for Pro / not-ready

    var html = '';
    _presets.forEach(function(p){
        var isOrig = (p.value === 'original');
        var n = isOrig ? 0 : parseInt(p.value, 10);
        // V2.B maxOutputDim: hard tool-pipeline cap (e.g. AI model max input).
        // Skip the option entirely — different from source-disable (which is
        // user-conditional and shows the option as disabled-with-hint).
        if (!isOrig && _maxOutputDim > 0 && n > _maxOutputDim) return;
        var isUpscale = !isOrig && srcShorter > 0 && n > srcShorter;
        // Plan ceiling: an explicit resolution above the tier's cap is a paid
        // option — disabled + «Премиум». Distinct from the upscale-disable
        // (a source condition); this is a tier condition. Checked first so a
        // 4K option on a 4K source still reads as Premium, not as upscale.
        // Default: disable strictly-larger options so users can't ask for
        // upscale. Allow exact-match (n === srcShorter) — that's a no-op.
        // V2.A allowUpscale: keep all options enabled, mark upscale ones with
        // an "↑ upscale" hint instead of disabling.
        var overToolCap = isUpscale && _allowUpscale && _maxUpscaleDim > 0 && n > _maxUpscaleDim;
        var disabledUpscale = isUpscale && (!_allowUpscale || overToolCap);
        // ⚠⚠ The «Премиум» badge may sit ONLY on an option the plan would
        // actually enable. Until 2026-09-04 the plan check ran FIRST, so a
        // guest with a 624p clip read «1440p · Премиум», «4K · Премиум» on
        // options that no plan unlocks (the source is too small / the tool
        // caps its resize). Comment #1574: bought Премиум to «улучшить до
        // 2к и 4к», got «исходник: 624p» on the same rows, filed a refund
        // 20 minutes later. 7 of the 25 Премиум refunds since July sit on
        // this pair of tools. Same rule as the /video-upscaler server lane
        // (refund №21): a button may promise only what its purchase opens.
        // A 4K option on a 4K source still reads Премиум — there the plan IS
        // the only lock.
        var overPlan = !isOrig && n > ceiling && !disabledUpscale;
        var disabled = overPlan || disabledUpscale;
        var label = buildOptionLabel(p.value, _detected.width, _detected.height);
        // "Original" on a source taller than the ceiling: it stays SELECTABLE
        // (it never upscales), but its label discloses the cap so the free user
        // sees the encode will be limited — the disclosure the no-silent rule
        // requires for a capped source.
        if (isOrig && ceiling !== Infinity && srcShorter > ceiling) {
            label += ' · ' + tr('upto', { n: ceiling });
        }
        if (overPlan) label += ' · ' + tr('premium');
        // The tool's own resize ceiling is a different reason from «source is
        // smaller»: name the ceiling, so the row explains itself to a paying
        // user instead of looking like a plan that did not apply.
        else if (overToolCap) label += ' · ' + tr('upscale_cap', { n: _maxUpscaleDim });
        else if (disabledUpscale) label += ' · ' + tr('upscale_hint', { n: srcShorter });
        else if (isUpscale && _allowUpscale) label = '↑ ' + label;
        html += '<option value="' + p.value + '"'
              + (disabled ? ' disabled' : '')
              + '>' + label + '</option>';
    });
    _selectEl.innerHTML = html;

    // Restore previous selection if still valid (and not disabled).
    var prevOpt = prev ? _selectEl.querySelector('option[value="' + prev + '"]:not([disabled])') : null;
    if (prevOpt) {
        _selectEl.value = prev;
    } else {
        // Default to "original" — safest, no surprise downscale.
        _selectEl.value = 'original';
    }

    updateBadge();
}

function updateBadge() {
    if (!_badgeEl) return;
    if (!_detected.width || !_detected.height) {
        _badgeEl.textContent = '';
        _badgeEl.hidden = true;
        return;
    }
    var srcCls = classify(_detected.width, _detected.height);
    var srcLine = tr('source') + ': ' + formatDimensions(_detected.width, _detected.height) + ' (' + srcCls + ')';

    var dim = getOutputDimensions();
    if (dim.didDownscale) {
        var outCls = classify(dim.width, dim.height);
        _badgeEl.textContent = srcLine + ' → ' + formatDimensions(dim.width, dim.height) + ' (' + outCls + ')';
    } else if (dim.didUpscale) {
        // V2.A: distinguish upscale visually so users see they're asking for
        // pixel synthesis (real-vs-stretched quality depends on the tool —
        // enhance-video runs Real-ESRGAN, others would just bilinear stretch).
        var upCls = classify(dim.width, dim.height);
        _badgeEl.textContent = srcLine + ' ↑ ' + formatDimensions(dim.width, dim.height) + ' (' + upCls + ' · ' + tr('upscale_arrow') + ')';
    } else {
        _badgeEl.textContent = srcLine;
    }
    _badgeEl.hidden = false;
}

// ── Public API ────────────────────────────────────────────────────────

function setDetectedSource(width, height) {
    _detected.width = Math.max(0, Math.round(width || 0));
    _detected.height = Math.max(0, Math.round(height || 0));
    rebuildOptions();
    if (!_suppressTelemetry && _toolName && window.__ae) {
        try {
            window.__ae('resolution_detected', {
                tool: _toolName,
                width: _detected.width,
                height: _detected.height,
                classification: _detected.width ? classify(_detected.width, _detected.height) : ''
            });
        } catch(_){}
    }
}

// Async fallback — used when caller hasn't probed dimensions yet. Creates
// a hidden <video>, listens loadedmetadata, revokes the URL.
function detectSource(file) {
    return new Promise(function(resolve){
        if (!file || !file.size || !/^video\//.test(file.type || '')) {
            // No file or non-video MIME → bail without state mutation.
            resolve({ width: 0, height: 0 });
            return;
        }
        var url;
        try { url = URL.createObjectURL(file); } catch(_) { resolve({ width: 0, height: 0 }); return; }
        var v = document.createElement('video');
        var settled = false;
        var done = function(w, h){
            if (settled) return;
            settled = true;
            try { URL.revokeObjectURL(url); } catch(_){}
            try { v.src = ''; v.load(); } catch(_){}
            setDetectedSource(w, h);
            resolve({ width: w, height: h });
        };
        v.preload = 'metadata';
        v.muted = true;
        v.addEventListener('loadedmetadata', function(){
            done(v.videoWidth || 0, v.videoHeight || 0);
        }, { once: true });
        v.addEventListener('error', function(){ done(0, 0); }, { once: true });
        // 8s safety timeout — broken/streaming files shouldn't hang detection
        setTimeout(function(){ done(0, 0); }, 8000);
        v.src = url;
    });
}

function getDetectedSource() {
    return {
        width: _detected.width,
        height: _detected.height,
        classification: _detected.width ? classify(_detected.width, _detected.height) : ''
    };
}

function getCurrentValue() {
    return _selectEl ? _selectEl.value : 'original';
}

function getOutputDimensions() {
    var v = getCurrentValue();
    var ceiling = planCeiling();

    // "Original" (source-match): capped DOWN to the ceiling only, never up —
    // "Original" means the source even on an upscale tool. So allowUpscale is
    // forced false here regardless of the tool's setting.
    if (v === 'original' || v === '0' || v == null || v === '') {
        if (ceiling === Infinity) {
            return computeFor(_detected.width, _detected.height, 'original', {});
        }
        return computeFor(_detected.width, _detected.height, String(ceiling), { allowUpscale: false });
    }

    var n = parseInt(v, 10) || 0;
    var target = (ceiling === Infinity) ? n : Math.min(n, ceiling);
    return computeFor(_detected.width, _detected.height, String(target), { allowUpscale: _allowUpscale });
}

// FFmpeg scale filter for the current selection. Returns null when value
// is "original" — caller can omit -vf entirely (faster path: no scale stage,
// no decode/re-encode for resolution change).
//
// Default: emits a filter with `min()` clamp so the FFmpeg expression itself
// guarantees no upscale even if our cached _detected dimensions were stale.
// V2.A allowUpscale: drops the clamp so upscale targets are honored.
function getScaleFilter() {
    var v = getCurrentValue();
    var ceiling = planCeiling();

    if (v === 'original' || !v) {
        // No plan cap → true passthrough (caller may omit -vf entirely).
        if (ceiling === Infinity) return null;
        // Free tier: cap "Original" DOWN to the ceiling. buildScaleFilter's
        // non-upscale form already wraps the target in min(N, iw|ih), so FFmpeg
        // no-ops it at runtime when the source is smaller than the ceiling —
        // which means this is safe even when we never detected the dimensions.
        return buildScaleFilter(ceiling, { allowUpscale: false });
    }

    var n = parseInt(v, 10) || 0;
    var target = (ceiling === Infinity) ? n : Math.min(n, ceiling);
    if (!target) return null;
    // An explicit target at/below the ceiling keeps the tool's own upscale
    // policy; the min(target, ceiling) above guarantees it never exceeds it.
    return buildScaleFilter(target, { allowUpscale: _allowUpscale && target === n });
}

// The same choice, phrased for WebCodecsPipeline.run's `params.resolution`:
// the output's SHORTER side as a string (the pipeline's contract — and this
// module's), or 'original' when the output is the source size. null when the
// source size is not known yet: the caller keeps its own safe default.
//
// Why this exists (2026-09-25, /video-speed «hanya proses berjam-jam»): a
// tool translating the selection itself had two ways to get it wrong, and
// both were live.
//   · Gate the fast lane on getScaleFilter() being null. It is NEVER null
//     for a visitor under a plan ceiling — «Original» is capped too — so
//     the lane was dead for everyone but Premium: /video-speed 30 d, hw_used
//     128 runs, all Premium; 2 199 guest sessions on single-thread x264
//     (a 16-min 1080p file ≈ 4 h). /video-flip wrote the same trap down in
//     its own comment and dodged it there only.
//   · Hand the pipeline the raw select value. «Original» then skips the
//     ceiling on the fast lane while the FFmpeg lane applies it — one pick,
//     two different files.
// getOutputDimensions() already folds the ceiling and the no-upscale rule in;
// this only restates its answer in the pipeline's words. The pipeline's own
// resolution rule UPSCALES a smaller source (up to 1080p), so a raw ceiling
// number must never be passed for a source below it — hence the comparison
// with the detected source, not with the ceiling.
function getPipelineResolution() {
    var sw = _detected.width, sh = _detected.height;
    if (!sw || !sh) return null;
    var d = getOutputDimensions();
    if (!d || !d.width || !d.height) return null;
    var srcShort = Math.min(sw, sh) - (Math.min(sw, sh) % 2);
    var outShort = Math.min(d.width, d.height);
    return outShort === srcShort || outShort === Math.min(sw, sh) ? 'original' : String(outShort);
}

// Chain the resolution scale with extra video filters. Useful for tools
// that already build a -vf chain (blur, watermark, format conversion).
//   getScaleFilterChain('format=yuv420p,fps=30')
//     → "scale=...:flags=lanczos,format=yuv420p,fps=30" (when downscaling)
//     → "format=yuv420p,fps=30"                        (when "original")
function getScaleFilterChain(extra) {
    var s = getScaleFilter();
    if (s && extra) return s + ',' + extra;
    if (s) return s;
    return extra || null;
}

function init(opts) {
    opts = opts || {};
    _selectEl = document.getElementById(opts.selectId || 'resolution-select');
    _fieldEl  = document.getElementById(opts.fieldId  || 'resolution-field');
    _badgeEl  = document.getElementById(opts.badgeId  || 'resolution-badge');
    _labelEl  = _fieldEl ? _fieldEl.querySelector('label') : null;
    _toolName = opts.tool || (_selectEl && _selectEl.dataset.tool) || 'unknown';
    // V2.A: per-tool opt-in for upscale. Module default stays no-upscale.
    _allowUpscale = !!opts.allowUpscale;
    // V2.B: per-tool hard cap on the dropdown ladder. 0 = no cap.
    _maxOutputDim = parseInt(opts.maxOutputDim, 10) || 0;
    // V2.C: per-tool cap on how far UPSCALE may go (downscale unaffected).
    _maxUpscaleDim = parseInt(opts.maxUpscaleDim, 10) || 0;

    if (opts.presets && opts.presets.length) {
        _presets = opts.presets.map(function(p){
            return (p === 'original' || p === 0) ? { value: 'original' } : { value: parseInt(p, 10) };
        });
    }

    if (!_selectEl) return;

    // Wire change → telemetry + badge refresh.
    _selectEl.addEventListener('change', function(){
        updateBadge();
        if (window.__ae && _toolName) {
            try {
                window.__ae('resolution_changed', {
                    tool: _toolName,
                    value: _selectEl.value,
                    srcW: _detected.width,
                    srcH: _detected.height
                });
            } catch(_){}
        }
    });

    // Initial render — rebuild options without telemetry noise (we haven't
    // detected a source yet, so the only "change" is from the persisted
    // setting being restored).
    _suppressTelemetry = true;
    rebuildOptions();
    _suppressTelemetry = false;

    // Persist selection per-tool. Skip when tool wires its own __toolSettings
    // (the bind() call there is idempotent — second binding overrides cleanly).
    if (window.__toolSettings && _toolName && _toolName !== 'unknown') {
        try {
            window.__toolSettings.bind(_toolName, _selectEl, 'resolution', _selectEl.value);
        } catch(_){}
    }

    // The plan ceiling depends on window.__qualityTier, whose snapshot lands
    // from the async session-bootstrap fetch — usually AFTER init(). Rebuild
    // when it arrives so the ceiling actually applies, and so a Premium user
    // who resolved late gets the 4K options back. rebuildOptions() preserves a
    // still-valid selection, so this never clobbers a user's choice.
    document.addEventListener('tier-bootstrap:ready', function(){
        _suppressTelemetry = true;
        rebuildOptions();
        _suppressTelemetry = false;
    });
}

window.ResolutionModule = {
    init: init,
    detectSource: detectSource,
    setDetectedSource: setDetectedSource,
    getDetectedSource: getDetectedSource,
    getCurrentValue: getCurrentValue,
    getOutputDimensions: getOutputDimensions,
    getScaleFilter: getScaleFilter,
    getScaleFilterChain: getScaleFilterChain,
    getPipelineResolution: getPipelineResolution,
    classify: classify,
    formatDimensions: formatDimensions,
    computeFor: computeFor,
    buildScaleFilter: buildScaleFilter,
    version: '1.3.0' // V2.A allowUpscale + V2.B maxOutputDim + honest Премиум badge
};

if (typeof window.__drainSharedModule === 'function') window.__drainSharedModule('ResolutionModule');

})();
